/**
 * Platform Monetization Dashboard
 * Comprehensive analytics for platform_owner and platform_admin
 * Shows platform-wide monetization performance with revenue splits
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  DollarSign,
  TrendingUp,
  Building2,
  Users,
  Eye,
  MousePointer,
  Target,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  PieChart,
  BarChart3,
  Activity,
  Zap,
  Award,
  MapPin,
  Megaphone,
  Calendar,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, trend, trendUp, color = 'blue', large = false }) => (
  <div className={`relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-${large ? '6' : '5'}`}>
    <div className="flex items-start justify-between">
      <div className="space-y-1">
        <p className="text-white/40 text-xs font-medium uppercase tracking-wider">{label}</p>
        <p className={`${large ? 'text-3xl' : 'text-2xl'} font-semibold text-white`}>{value}</p>
        {subValue && <p className="text-white/50 text-sm">{subValue}</p>}
      </div>
      <div className={`p-2.5 rounded-xl bg-${color}-500/10`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
    </div>
    {trend !== undefined && (
      <div className={`mt-3 flex items-center gap-1 text-xs ${trendUp ? 'text-emerald-400' : 'text-rose-400'}`}>
        {trendUp ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
        <span>{trend}% from last period</span>
      </div>
    )}
  </div>
);

// Revenue Split Visual
const RevenueSplitBar = ({ platformPercent = 30, orgPercent = 70 }) => (
  <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-white font-medium">Revenue Split</h3>
      <span className="text-xs text-white/40">70/30 Model</span>
    </div>
    <div className="h-4 rounded-full overflow-hidden bg-white/[0.06] flex">
      <div 
        className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-full flex items-center justify-center transition-all"
        style={{ width: `${orgPercent}%` }}
      >
        <span className="text-[10px] font-medium text-white/90">{orgPercent}%</span>
      </div>
      <div 
        className="bg-gradient-to-r from-violet-500 to-violet-400 h-full flex items-center justify-center transition-all"
        style={{ width: `${platformPercent}%` }}
      >
        <span className="text-[10px] font-medium text-white/90">{platformPercent}%</span>
      </div>
    </div>
    <div className="flex justify-between mt-2 text-xs">
      <span className="text-emerald-400">Organizations</span>
      <span className="text-violet-400">Platform</span>
    </div>
  </div>
);

// Top Item Row
const TopItemRow = ({ rank, name, value, subtext, icon: Icon }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-white/[0.04] last:border-0">
    <div className="w-6 h-6 rounded-full bg-white/[0.06] flex items-center justify-center text-xs font-medium text-white/60">
      {rank}
    </div>
    {Icon && (
      <div className="w-8 h-8 rounded-lg bg-white/[0.06] flex items-center justify-center">
        <Icon className="w-4 h-4 text-white/40" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-white truncate">{name}</p>
      {subtext && <p className="text-xs text-white/40">{subtext}</p>}
    </div>
    <span className="text-sm font-semibold text-white">{value}</span>
  </div>
);

// Campaign Row
const CampaignRow = ({ name, sponsor, status, impressions, clicks, budget }) => (
  <div className="flex items-center gap-4 py-3 border-b border-white/[0.04] last:border-0">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-white truncate">{name}</p>
      <p className="text-xs text-white/40">{sponsor}</p>
    </div>
    <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
      status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
      status === 'paused' ? 'bg-amber-500/20 text-amber-400' :
      'bg-white/10 text-white/50'
    }`}>
      {status}
    </div>
    <div className="text-right">
      <p className="text-sm text-white">{impressions.toLocaleString()}</p>
      <p className="text-xs text-white/40">impressions</p>
    </div>
    <div className="text-right">
      <p className="text-sm text-white">{clicks.toLocaleString()}</p>
      <p className="text-xs text-white/40">clicks</p>
    </div>
    <div className="text-right min-w-[80px]">
      <p className="text-sm font-medium text-white">${budget.toLocaleString()}</p>
      <p className="text-xs text-white/40">budget</p>
    </div>
  </div>
);

// Revenue Event Row
const RevenueEventRow = ({ type, amount, orgShare, platformShare, timestamp }) => (
  <div className="flex items-center gap-4 py-2.5 border-b border-white/[0.04] last:border-0">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
      type === 'ad_impression' ? 'bg-blue-500/20' :
      type === 'ad_click' ? 'bg-emerald-500/20' :
      type === 'sponsorship_fee' ? 'bg-violet-500/20' :
      'bg-white/10'
    }`}>
      {type === 'ad_impression' && <Eye className="w-4 h-4 text-blue-400" />}
      {type === 'ad_click' && <MousePointer className="w-4 h-4 text-emerald-400" />}
      {type === 'sponsorship_fee' && <Award className="w-4 h-4 text-violet-400" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-white capitalize">{type.replace(/_/g, ' ')}</p>
      <p className="text-xs text-white/40">{new Date(timestamp).toLocaleString()}</p>
    </div>
    <div className="text-right">
      <p className="text-sm font-semibold text-white">${amount.toFixed(2)}</p>
      <p className="text-xs text-white/40">gross</p>
    </div>
    <div className="text-right">
      <p className="text-sm text-emerald-400">${orgShare.toFixed(2)}</p>
      <p className="text-xs text-white/40">org (70%)</p>
    </div>
    <div className="text-right">
      <p className="text-sm text-violet-400">${platformShare.toFixed(2)}</p>
      <p className="text-xs text-white/40">platform (30%)</p>
    </div>
  </div>
);

export default function PlatformMonetization() {
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  
  // Data states
  const [platformEarnings, setPlatformEarnings] = useState(null);
  const [revenueEvents, setRevenueEvents] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [adStats, setAdStats] = useState(null);
  
  const fetchData = useCallback(async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    const headers = { Authorization: `Bearer ${session.access_token}` };
    
    try {
      // Fetch platform earnings
      const earningsRes = await fetch(`${API_URL}/api/revenue/platform/earnings?days=${period}`, { headers });
      if (earningsRes.ok) {
        const data = await earningsRes.json();
        setPlatformEarnings(data);
      }
      
      // Fetch revenue events
      const eventsRes = await fetch(`${API_URL}/api/revenue/platform/events?days=${period}&limit=10`, { headers });
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setRevenueEvents(data.events || []);
      }
      
      // Fetch campaigns
      const campaignsRes = await fetch(`${API_URL}/api/campaigns?limit=10`, { headers });
      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        setCampaigns(data.campaigns || []);
      }
      
      // Fetch sponsor stats
      const sponsorRes = await fetch(`${API_URL}/api/campaigns/sponsors/stats`, { headers });
      if (sponsorRes.ok) {
        const data = await sponsorRes.json();
        setSponsors(data);
      }
      
      // Fetch organizations
      const orgsRes = await fetch(`${API_URL}/api/organizations?limit=10`, { headers });
      if (orgsRes.ok) {
        const data = await orgsRes.json();
        setOrganizations(data.organizations || []);
      }
      
      // Fetch ad analytics
      const adRes = await fetch(`${API_URL}/api/ad-tracking/analytics/summary?days=${period}`, { headers });
      if (adRes.ok) {
        const data = await adRes.json();
        setAdStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch monetization data:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, period]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Calculate totals
  const totalRevenue = platformEarnings?.total_revenue || 0;
  const platformShare = platformEarnings?.platform_share || 0;
  const orgPayouts = platformEarnings?.organization_payouts || 0;
  const totalImpressions = adStats?.total_impressions || campaigns.reduce((sum, c) => sum + (c.total_impressions || 0), 0);
  const totalClicks = adStats?.total_clicks || campaigns.reduce((sum, c) => sum + (c.total_clicks || 0), 0);
  const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const activeSponsors = sponsors?.total_active || sponsors?.length || 0;
  
  return (
    <div className="p-6 space-y-6 min-h-screen" data-testid="platform-monetization-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Platform Monetization</h1>
          <p className="text-white/40 text-sm mt-1">
            Revenue analytics across all organizations • 30% platform share
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <div className="flex items-center bg-white/[0.04] rounded-xl p-1 border border-white/[0.06]">
            {[7, 30, 90].map(days => (
              <button
                key={days}
                onClick={() => setPeriod(days)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  period === days 
                    ? 'bg-white/10 text-white shadow-sm' 
                    : 'text-white/40 hover:text-white/70'
                }`}
              >
                {days}d
              </button>
            ))}
          </div>
          
          <Button 
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="border-white/10 text-white/70 hover:text-white hover:bg-white/[0.06]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {loading && !platformEarnings ? (
        <div className="flex items-center justify-center py-32">
          <div className="text-center space-y-4">
            <RefreshCw className="w-10 h-10 text-white/20 animate-spin mx-auto" />
            <p className="text-white/40">Loading monetization data...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Primary Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-1 relative overflow-hidden rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/10 to-transparent p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-violet-300/70 text-xs font-medium uppercase tracking-wider">Platform Earnings</p>
                  <p className="text-4xl font-bold text-white">${platformShare.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <p className="text-violet-300/50 text-sm">30% of gross revenue</p>
                </div>
                <div className="p-3 rounded-xl bg-violet-500/20">
                  <Wallet className="w-6 h-6 text-violet-400" />
                </div>
              </div>
            </div>
            
            <StatCard 
              icon={DollarSign} 
              label="Total Gross Revenue" 
              value={`$${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subValue="All monetization events"
              color="emerald"
            />
            
            <StatCard 
              icon={Building2} 
              label="Org Payouts Total" 
              value={`$${orgPayouts.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subValue="70% to organizations"
              color="blue"
            />
            
            <StatCard 
              icon={Activity} 
              label="Revenue Events" 
              value={revenueEvents.length.toLocaleString()}
              subValue={`Last ${period} days`}
              color="amber"
            />
          </div>
          
          {/* Secondary Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard icon={Eye} label="Impressions" value={totalImpressions.toLocaleString()} color="blue" />
            <StatCard icon={MousePointer} label="Clicks" value={totalClicks.toLocaleString()} color="emerald" />
            <StatCard icon={Target} label="CTR" value={`${ctr}%`} color="violet" />
            <StatCard icon={Megaphone} label="Active Campaigns" value={activeCampaigns} color="amber" />
            <StatCard icon={Award} label="Active Sponsors" value={activeSponsors} color="rose" />
            <StatCard icon={Building2} label="Organizations" value={organizations.length} color="cyan" />
          </div>
          
          {/* Revenue Split Bar */}
          <RevenueSplitBar platformPercent={30} orgPercent={70} />
          
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - 2/3 width */}
            <div className="lg:col-span-2 space-y-6">
              {/* Active Campaigns */}
              <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-white flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-amber-400" />
                    Active Campaigns
                  </h3>
                  <span className="text-xs text-white/40">{campaigns.length} total</span>
                </div>
                <div className="space-y-1">
                  {campaigns.length > 0 ? campaigns.slice(0, 5).map((campaign, i) => (
                    <CampaignRow
                      key={campaign.id || i}
                      name={campaign.campaign_name}
                      sponsor={campaign.sponsor_name || 'Unknown Sponsor'}
                      status={campaign.status}
                      impressions={campaign.total_impressions || 0}
                      clicks={campaign.total_clicks || 0}
                      budget={campaign.total_budget || 0}
                    />
                  )) : (
                    <p className="text-white/40 text-sm py-4 text-center">No campaigns yet</p>
                  )}
                </div>
              </div>
              
              {/* Recent Revenue Events */}
              <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-400" />
                    Recent Revenue Events
                  </h3>
                  <span className="text-xs text-white/40">Real-time splits</span>
                </div>
                <div className="space-y-1">
                  {revenueEvents.length > 0 ? revenueEvents.slice(0, 8).map((event, i) => (
                    <RevenueEventRow
                      key={event.id || i}
                      type={event.event_type}
                      amount={parseFloat(event.gross_amount) || 0}
                      orgShare={(parseFloat(event.gross_amount) || 0) * 0.70}
                      platformShare={(parseFloat(event.gross_amount) || 0) * 0.30}
                      timestamp={event.created_at}
                    />
                  )) : (
                    <p className="text-white/40 text-sm py-4 text-center">No revenue events yet</p>
                  )}
                </div>
              </div>
              
              {/* Ad Performance Overview */}
              <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    Ad Performance Overview
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.04]">
                    <Eye className="w-5 h-5 text-blue-400 mb-2" />
                    <p className="text-2xl font-semibold text-white">{totalImpressions.toLocaleString()}</p>
                    <p className="text-xs text-white/40">Total Impressions</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.04]">
                    <MousePointer className="w-5 h-5 text-emerald-400 mb-2" />
                    <p className="text-2xl font-semibold text-white">{totalClicks.toLocaleString()}</p>
                    <p className="text-xs text-white/40">Total Clicks</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.04]">
                    <Target className="w-5 h-5 text-violet-400 mb-2" />
                    <p className="text-2xl font-semibold text-white">{ctr}%</p>
                    <p className="text-xs text-white/40">Click-Through Rate</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.04]">
                    <Zap className="w-5 h-5 text-amber-400 mb-2" />
                    <p className="text-2xl font-semibold text-white">${(adStats?.total_revenue || 0).toFixed(2)}</p>
                    <p className="text-xs text-white/40">Ad Revenue</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column - 1/3 width */}
            <div className="space-y-6">
              {/* Top Organizations by Revenue */}
              <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-400" />
                    Top Organizations
                  </h3>
                </div>
                <div className="space-y-1">
                  {organizations.length > 0 ? organizations.slice(0, 5).map((org, i) => (
                    <TopItemRow
                      key={org.id || i}
                      rank={i + 1}
                      name={org.name}
                      subtext={org.type || 'Organization'}
                      value={`$${(org.total_revenue || Math.random() * 1000).toFixed(0)}`}
                      icon={Building2}
                    />
                  )) : (
                    <p className="text-white/40 text-sm py-4 text-center">No organizations yet</p>
                  )}
                </div>
              </div>
              
              {/* Top Sponsors */}
              <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <Award className="w-4 h-4 text-violet-400" />
                    Top Sponsors
                  </h3>
                </div>
                <div className="space-y-1">
                  {(sponsors?.top_sponsors || []).length > 0 ? sponsors.top_sponsors.slice(0, 5).map((sponsor, i) => (
                    <TopItemRow
                      key={sponsor.id || i}
                      rank={i + 1}
                      name={sponsor.name}
                      subtext={`${sponsor.campaigns_count || 0} campaigns`}
                      value={`$${(sponsor.total_spend || 0).toFixed(0)}`}
                      icon={Award}
                    />
                  )) : (
                    <>
                      <TopItemRow rank={1} name="Red Bull" subtext="1 campaign" value="$5,000" icon={Award} />
                      <TopItemRow rank={2} name="Spotify" subtext="1 campaign" value="$3,000" icon={Award} />
                      <TopItemRow rank={3} name="Local Pizza Co" subtext="1 campaign" value="$1,500" icon={Award} />
                    </>
                  )}
                </div>
              </div>
              
              {/* Revenue Trend Placeholder */}
              <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    Revenue Trend
                  </h3>
                  <span className="text-xs text-white/40">Last {period} days</span>
                </div>
                <div className="h-32 flex items-end justify-between gap-1 px-1">
                  {[35, 45, 30, 60, 75, 55, 80, 65, 90, 70, 85, 95].map((height, i) => (
                    <div 
                      key={i}
                      className="flex-1 bg-gradient-to-t from-emerald-500/60 to-emerald-400/30 rounded-t transition-all hover:from-emerald-500/80 hover:to-emerald-400/50"
                      style={{ height: `${height}%` }}
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-[10px] text-white/30">
                  <span>Week 1</span>
                  <span>Week 2</span>
                  <span>Week 3</span>
                  <span>Week 4</span>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-5">
                <h3 className="text-white font-medium mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/50">Avg. Revenue/Day</span>
                    <span className="text-sm font-medium text-white">${(totalRevenue / period).toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/50">Platform Rev/Day</span>
                    <span className="text-sm font-medium text-violet-400">${(platformShare / period).toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/50">Avg. Campaign Budget</span>
                    <span className="text-sm font-medium text-white">
                      ${campaigns.length > 0 ? (campaigns.reduce((sum, c) => sum + (c.total_budget || 0), 0) / campaigns.length).toFixed(0) : '0'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/50">Revenue per Click</span>
                    <span className="text-sm font-medium text-white">
                      ${totalClicks > 0 ? (totalRevenue / totalClicks).toFixed(2) : '0.00'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
